import React, { useState } from 'react';
import MessageInput from './MessageInput';
import ContactInfo from './ContactInfo';
import { Mic, FileText, User } from 'lucide-react';

interface Message {
    id: string;
    text: string;
    isAgent: boolean;
    timestamp: string;
    avatar?: string;
    mediaUrl?: string | null;
}

interface ChatMessagesProps {
    messages: Message[];
    onSendMessage: (message: string) => void;
    contactName?: string;
}

// Detectar tipo de media desde la URL
const getMediaType = (url: string | null | undefined): 'audio' | 'image' | 'video' | 'document' | 'text' => {
    if (!url) return 'text';

    const lowerUrl = url.toLowerCase();

    // Audio
    if (lowerUrl.includes('.ogg') || lowerUrl.includes('.mp3') || lowerUrl.includes('.wav') ||
        lowerUrl.includes('.m4a') || lowerUrl.includes('.opus') || lowerUrl.includes('ptt')) {
        return 'audio';
    }

    // Imagen
    if (lowerUrl.includes('.jpg') || lowerUrl.includes('.jpeg') || lowerUrl.includes('.png') ||
        lowerUrl.includes('.gif') || lowerUrl.includes('.webp')) {
        return 'image';
    }

    // Video
    if (lowerUrl.includes('.mp4') || lowerUrl.includes('.mov') || lowerUrl.includes('.avi') ||
        lowerUrl.includes('.webm')) {
        return 'video';
    }

    // Documento (PDF, Word, etc)
    if (lowerUrl.includes('.pdf') || lowerUrl.includes('.doc') || lowerUrl.includes('.docx') ||
        lowerUrl.includes('.xls') || lowerUrl.includes('.xlsx')) {
        return 'document';
    }

    return 'text';
};

// Componente para renderizar audio
const AudioBubble: React.FC<{ url: string; isAgent: boolean }> = ({ url, isAgent }) => (
    <div className={`max-w-xs lg:max-w-md rounded-2xl overflow-hidden ${isAgent ? 'bg-maxi-green rounded-br-md' : 'bg-white rounded-bl-md'
        }`}>
        <div className="p-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                <Mic className="h-5 w-5 text-gray-600" />
            </div>
            <audio controls className="h-10 max-w-[200px]">
                <source src={url} type="audio/ogg" />
                Tu navegador no soporta audio
            </audio>
        </div>
    </div>
);

// Componente para renderizar imagen
const ImageBubble: React.FC<{ url: string; isAgent: boolean; caption?: string }> = ({ url, isAgent, caption }) => (
    <div className={`max-w-xs lg:max-w-md rounded-2xl overflow-hidden ${isAgent ? 'bg-maxi-green rounded-br-md' : 'bg-white rounded-bl-md'
        }`}>
        <img
            src={url}
            alt="Imagen"
            className="w-full max-w-[250px] h-auto"
            onError={(e) => {
                (e.target as HTMLImageElement).src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="150"><rect fill="%23ddd" width="200" height="150"/><text x="50%" y="50%" fill="%23999" text-anchor="middle" dy=".3em">Error</text></svg>';
            }}
        />
        {caption && caption !== 'imageMessage' && (
            <p className="px-3 py-2 text-sm">{caption}</p>
        )}
    </div>
);

// Componente para renderizar video
const VideoBubble: React.FC<{ url: string; isAgent: boolean; caption?: string }> = ({ url, isAgent, caption }) => (
    <div className={`max-w-xs lg:max-w-md rounded-2xl overflow-hidden ${isAgent ? 'bg-maxi-green rounded-br-md' : 'bg-white rounded-bl-md'
        }`}>
        <div className="relative">
            <video
                src={url}
                className="w-full max-w-[250px] h-auto"
                controls
            />
        </div>
        {caption && caption !== 'videoMessage' && (
            <p className="px-3 py-2 text-sm">{caption}</p>
        )}
    </div>
);

// Componente para renderizar documento
const DocumentBubble: React.FC<{ url: string; isAgent: boolean; caption?: string }> = ({ url, isAgent, caption }) => {
    const fileName = url.split('/').pop() || 'documento';

    return (
        <div className={`max-w-xs lg:max-w-md rounded-2xl overflow-hidden ${isAgent ? 'bg-maxi-green rounded-br-md' : 'bg-white rounded-bl-md'
            }`}>
            <a href={url} target="_blank" rel="noopener noreferrer" className="block p-3">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded bg-gray-200 flex items-center justify-center flex-shrink-0">
                        <FileText className="h-5 w-5 text-gray-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{fileName}</p>
                        <p className="text-xs text-gray-500">Documento</p>
                    </div>
                </div>
            </a>
            {caption && caption !== 'documentMessage' && (
                <p className="px-3 pb-2 text-sm">{caption}</p>
            )}
        </div>
    );
};

const ChatMessages: React.FC<ChatMessagesProps> = ({ messages, onSendMessage, contactName }) => {
    const [showContactModal, setShowContactModal] = useState(false);

    const contact = {
        name: contactName || 'Contacto',
        company: 'Empresa S.A.',
        email: 'contacto@email.com',
        phone: '+57 300 123 4567',
        value: '$10,000',
        stage: 'seguimiento',
    };

    const renderMessageContent = (message: Message) => {
        const mediaType = getMediaType(message.mediaUrl);

        console.log('[ChatMessages] 🎨 Renderizando mensaje:', {
            id: message.id,
            text: message.text,
            mediaUrl: message.mediaUrl,
            mediaType
        });

        switch (mediaType) {
            case 'audio':
                return <AudioBubble url={message.mediaUrl!} isAgent={message.isAgent} />;
            case 'image':
                return <ImageBubble url={message.mediaUrl!} isAgent={message.isAgent} caption={message.text} />;
            case 'video':
                return <VideoBubble url={message.mediaUrl!} isAgent={message.isAgent} caption={message.text} />;
            case 'document':
                return <DocumentBubble url={message.mediaUrl!} isAgent={message.isAgent} caption={message.text} />;
            default:
                return (
                    <div
                        className={`max-w-xs lg:max-w-md px-4 py-3 rounded-2xl ${message.isAgent
                            ? 'bg-maxi-green text-black rounded-br-md'
                            : 'bg-white text-maxi-text-dark rounded-bl-md'
                            }`}
                    >
                        <p className="text-sm whitespace-pre-wrap break-words">{message.text}</p>
                    </div>
                );
        }
    };

    return (
        <div className="flex flex-col h-full min-h-0 bg-white px-8 py-6 relative rounded-b-2xl">
            <div className="flex-1 overflow-y-auto bg-maxi-gray-light pb-20">
                <div className="space-y-4 max-w-full">
                    {messages.map((message) => (
                        <div
                            key={message.id}
                            className={`flex flex-col ${message.isAgent ? 'items-end' : 'items-start'}`}
                        >
                            <div className={`flex items-start gap-3 ${message.isAgent ? 'justify-end' : 'justify-start'}`}>
                                {!message.isAgent && (
                                    <div className="w-10 h-10 rounded-full bg-gray-300 flex-shrink-0 overflow-hidden">
                                        {message.avatar ? (
                                            <img
                                                src={message.avatar}
                                                alt="User avatar"
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center bg-gray-400">
                                                <User className="h-5 w-5 text-white" />
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Rounded background wrapper for the message */}
                                <div className={`rounded-full p-1 ${message.isAgent ? 'bg-maxi-green/10' : 'bg-gray-100'} shadow-sm`}>
                                    {renderMessageContent(message)}
                                </div>

                                {message.isAgent && (
                                    <div className="w-10 h-10 rounded-full bg-gray-300 flex-shrink-0 overflow-hidden">
                                        {message.avatar ? (
                                            <img
                                                src={message.avatar}
                                                alt="Agent avatar"
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center bg-gray-400">
                                                <User className="h-5 w-5 text-white" />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            {/* Timestamp debajo del mensaje */}
                            <span className={`text-xs text-muted-foreground mt-1 ${message.isAgent ? 'mr-14' : 'ml-14'}`}>
                                {message.timestamp}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
            <div className="sticky bottom-0 bg-white py-3">
                <MessageInput onSendMessage={onSendMessage} />
            </div>

            {/* Contact Info Modal */}
            {showContactModal && (
                <div className="fixed inset-0 z-50 flex items-end justify-end">
                    <div className="bg-black bg-opacity-30 absolute inset-0" onClick={() => setShowContactModal(false)} />
                    <div className="relative h-full flex items-center">
                        <div className="animate-slide-in-right h-full flex items-center">
                            <ContactInfo contact={contact} onEdit={() => { }} />
                        </div>
                        <button
                            className="absolute top-4 right-4 text-maxi-text-dark bg-white rounded-full p-2 shadow"
                            onClick={() => setShowContactModal(false)}
                        >
                            ×
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ChatMessages;
